import { useEffect, useRef, useState, useCallback } from "react";
import { flushSync } from "react-dom";
import moment from "moment";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError, InteractionStatus } from "@azure/msal-browser";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Icon,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
} from "@mui/material";

import { loginRequest } from "../authConfig";
import { createInvite } from "../api/FileRequestAPI";
import { submitFeedback, submitCorrection } from "../api/OcrAPI";
import Lightbox from "../components/Lightbox";
import OcrResultsPanel from "../components/OcrResultsPanel";
import OcrCorrectionForm from "../components/OcrCorrectionForm";

type Log = {
  type: string;
  time: Date;
  body: string;
};

type LogTableProps = {
  wssURL: string;
  messages: Log[];
};

type ImageFile = {
  url: string;
  title: string;
  fileId?: string;
};

type OcrResult = {
  resultId: string;
  fileId: string;
  inviteId: string;
  extractedData?: any;
  status: "processing" | "completed" | "failed" | "verified" | "corrected";
  confidenceScore?: number;
  feedback?: {
    rating: "up" | "down";
    timestamp: string;
    userId: string;
  };
  correctedData?: any;
  correctionComments?: string;
  errorMessage?: string;
};

const isValidPhone = (phone: string) => /^\d{10}$/.test(phone);

function FileRequest() {
  const [name, setName] = useState("Patient");
  const [method] = useState("sms");
  const [address, setAddress] = useState("");
  const [inviteId, setInviteId] = useState("");
  const [wssURL, setWssURL] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<ImageFile[]>([]);
  const uploadedFilesRef = useRef<ImageFile[]>([]); // Add ref to track current files
  const [isSending, setIsSending] = useState(false);
  const [showImageURL, setShowImageURL] = useState("");
  const [messages, setMessages] = useState<Log[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const ws = useRef<WebSocket | null>(null);

  // OCR state
  const [ocrResults, setOcrResults] = useState<Map<string, OcrResult>>(new Map());
  const [editingFileId, setEditingFileId] = useState<string | null>(null);
  const [ocrLoading, setOcrLoading] = useState<Set<string>>(new Set());

  const { instance, inProgress, accounts } = useMsal();

  // Keep ref in sync with state
  useEffect(() => {
    uploadedFilesRef.current = uploadedFiles;
  }, [uploadedFiles]);

  useEffect(() => {
    if (!wssURL) return;

    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/8774a9f2-9bcd-4407-9c5d-a8a9b2dc10f3',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'FileRequest.tsx:useEffect',message:'WS useEffect MOUNT',data:{wssURL,inviteId},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H2'})}).catch(()=>{});
    // #endregion

    ws.current = new WebSocket(wssURL);
    ws.current.onopen = () => {
      // send SMS
      if (ws.current) ws.current.send("sms");
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/8774a9f2-9bcd-4407-9c5d-a8a9b2dc10f3',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'FileRequest.tsx:onopen',message:'WS OPENED',data:{readyState:ws.current?.readyState},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H1'})}).catch(()=>{});
      // #endregion
    };
    ws.current.onclose = (ev) => {
      console.log("ws closed");
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/8774a9f2-9bcd-4407-9c5d-a8a9b2dc10f3',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'FileRequest.tsx:onclose',message:'WS CLOSED',data:{code:ev.code,reason:ev.reason,wasClean:ev.wasClean},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H2'})}).catch(()=>{});
      // #endregion
    };
    ws.current.onerror = (ev) => {
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/8774a9f2-9bcd-4407-9c5d-a8a9b2dc10f3',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'FileRequest.tsx:onerror',message:'WS ERROR',data:{},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H1'})}).catch(()=>{});
      // #endregion
    };
    ws.current.onmessage = (e) => {
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/8774a9f2-9bcd-4407-9c5d-a8a9b2dc10f3',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'FileRequest.tsx:onmessage:raw',message:'WS RAW MESSAGE',data:{rawDataLength:e.data?.length,rawDataPreview:typeof e.data==='string'?e.data.substring(0,200):'non-string',readyState:ws.current?.readyState},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H1'})}).catch(()=>{});
      // #endregion

      try {
        const message = JSON.parse(e.data);
        
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/8774a9f2-9bcd-4407-9c5d-a8a9b2dc10f3',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'FileRequest.tsx:onmessage:parsed',message:'WS PARSED MESSAGE',data:{type:message.type,bodyType:typeof message.body,bodyPreview:typeof message.body==='string'?message.body.substring(0,100):'object'},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H1'})}).catch(()=>{});
        // #endregion
        
        if (message.type === "FileDownloadUrlCreated") {
          // body can be a string (legacy: just the URL) or an object { url, fileId }
          const url = typeof message.body === 'object' ? message.body.url : message.body;
          const fileId = typeof message.body === 'object' ? message.body.fileId : message.fileId;
          
          // #region agent log
          fetch('http://127.0.0.1:7243/ingest/8774a9f2-9bcd-4407-9c5d-a8a9b2dc10f3',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'FileRequest.tsx:FileDownloadUrlCreated',message:'PROCESSING FileDownloadUrlCreated',data:{hasUrl:!!url,urlLength:url?.length,fileId,currentFilesCount:uploadedFilesRef.current.length},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H4'})}).catch(()=>{});
          // #endregion
          
          flushSync(() => {
            setUploadedFiles((currentFiles) => {
              const urlExists = currentFiles.some(file => file.url === url);
              if (urlExists) return currentFiles;
              
              const newFile = { 
                url: url, 
                title: `File ${currentFiles.length + 1}`,
                fileId: fileId
              };
              const updatedFiles = [...currentFiles, newFile];
              uploadedFilesRef.current = updatedFiles;
              return updatedFiles;
            });
          });
          return;
        }

        // Handle OCR WebSocket events
        if (message.type === "OcrProcessingStarted") {
          const { resultId, fileId } = message.body;
          console.log("OCR processing started:", resultId, fileId);
          setOcrResults((prev) => {
            const newMap = new Map(prev);
            newMap.set(fileId, {
              resultId,
              fileId,
              inviteId,
              status: "processing",
            });
            return newMap;
          });
          return;
        }

        if (message.type === "OcrProcessingCompleted") {
          const { resultId, fileId, extractedData, confidenceScore } = message.body;
          console.log("OCR processing completed:", resultId, fileId);
          setOcrResults((prev) => {
            const newMap = new Map(prev);
            newMap.set(fileId, {
              resultId,
              fileId,
              inviteId,
              extractedData,
              status: "completed",
              confidenceScore,
            });
            return newMap;
          });
          return;
        }

        if (message.type === "OcrProcessingFailed") {
          const { resultId, fileId, errorMessage } = message.body;
          console.log("OCR processing failed:", resultId, fileId, errorMessage);
          setOcrResults((prev) => {
            const newMap = new Map(prev);
            newMap.set(fileId, {
              resultId,
              fileId,
              inviteId,
              status: "failed",
              errorMessage,
            });
            return newMap;
          });
          return;
        }

        setMessages((currentMessages) => [message, ...currentMessages]);
      } catch (error) {
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/8774a9f2-9bcd-4407-9c5d-a8a9b2dc10f3',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'FileRequest.tsx:onmessage:error',message:'WS MESSAGE ERROR',data:{error:String(error),rawDataPreview:typeof e.data==='string'?e.data.substring(0,200):'non-string'},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H3'})}).catch(()=>{});
        // #endregion
        console.error("Error parsing WebSocket message:", error, e.data);
      }
    };
    const wsCurrent = ws.current;
    return () => {
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/8774a9f2-9bcd-4407-9c5d-a8a9b2dc10f3',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'FileRequest.tsx:useEffect:cleanup',message:'WS useEffect CLEANUP - closing WS',data:{wssURL,inviteId,readyState:wsCurrent?.readyState},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H2'})}).catch(()=>{});
      // #endregion
      wsCurrent.close();
      console.log("ws closed");
    };
  }, [wssURL, inviteId]);

  const sendRequest = useCallback(() => {
    if ((inviteId && wssURL) && inProgress === InteractionStatus.None) return;

    setIsSending(true);

    const accessTokenRequest = {
      account: accounts[0],
      ...loginRequest,
    };

    instance
      .acquireTokenSilent(accessTokenRequest)
      .then((accessTokenResponse) => {
        const { accessToken, idToken } = accessTokenResponse;

        createInvite(
            name,
            method,
            address,
            accessToken,
            idToken
        ).then((response) => {
          setMessages((currentMessages) => [
            { type: "messageCreated", body: "Message created", time: new Date() },
            ...currentMessages,
          ]);
          setInviteId(response.inviteId);
          setWssURL(`${response.wssUrl}?inviteId=${response.inviteId}`);
        });
      })
      .catch((error) => {
        if (error instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect(accessTokenRequest);
        }

        console.error("sendRequestError", error);
      }).finally(() => {
        setIsSending(false);
      });
  }, [address, accounts, instance, method, name, inProgress, inviteId, wssURL]);

  const sendSMS = async () => {
    setIsSending(true);
    if (ws.current) ws.current.send("sms");
    setTimeout(() => {
      setIsSending(false);
    }, 2000);
  };

  const completeRequest = async () => {
    setName("");
    setInviteId("");
    setAddress("");
    setWssURL("");
    setMessages([]);
    setUploadedFiles([]);
    uploadedFilesRef.current = []; // Clear ref as well
    setOcrResults(new Map()); // Clear OCR results
    setEditingFileId(null); // Clear editing state
  };

  // OCR action handlers
  const handleOcrApprove = useCallback(async (fileId: string, resultId: string) => {
    if (inProgress !== InteractionStatus.None) return;

    const accessTokenRequest = {
      account: accounts[0],
      ...loginRequest,
    };

    setOcrLoading((prev) => new Set(prev).add(fileId));

    try {
      const accessTokenResponse = await instance.acquireTokenSilent(accessTokenRequest);
      const { accessToken, idToken } = accessTokenResponse;
      const userId = accounts[0]?.username || "unknown";

      await submitFeedback(resultId, inviteId, "up", userId, accessToken, idToken);
      
      // Update local state
      setOcrResults((prev) => {
        const newMap = new Map(prev);
        const result = newMap.get(fileId);
        if (result) {
          newMap.set(fileId, {
            ...result,
            status: "verified",
            feedback: {
              rating: "up",
              timestamp: new Date().toISOString(),
              userId,
            },
          });
        }
        return newMap;
      });

      console.log("OCR result approved for fileId:", fileId);
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        instance.acquireTokenRedirect(accessTokenRequest);
      }
      console.error("Error approving OCR result:", error);
    } finally {
      setOcrLoading((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });
    }
  }, [inviteId, accounts, instance, inProgress]);

  const handleOcrReject = useCallback(async (fileId: string, resultId: string) => {
    if (inProgress !== InteractionStatus.None) return;

    const accessTokenRequest = {
      account: accounts[0],
      ...loginRequest,
    };

    setOcrLoading((prev) => new Set(prev).add(fileId));

    try {
      const accessTokenResponse = await instance.acquireTokenSilent(accessTokenRequest);
      const { accessToken, idToken } = accessTokenResponse;
      const userId = accounts[0]?.username || "unknown";

      await submitFeedback(resultId, inviteId, "down", userId, accessToken, idToken);
      
      // Update local state
      setOcrResults((prev) => {
        const newMap = new Map(prev);
        const result = newMap.get(fileId);
        if (result) {
          newMap.set(fileId, {
            ...result,
            feedback: {
              rating: "down",
              timestamp: new Date().toISOString(),
              userId,
            },
          });
        }
        return newMap;
      });

      console.log("OCR result rejected for fileId:", fileId);
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        instance.acquireTokenRedirect(accessTokenRequest);
      }
      console.error("Error rejecting OCR result:", error);
    } finally {
      setOcrLoading((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });
    }
  }, [inviteId, accounts, instance, inProgress]);

  const handleOcrEdit = useCallback((fileId: string) => {
    setEditingFileId(fileId);
  }, []);

  const handleOcrSaveCorrection = useCallback(async (
    fileId: string,
    resultId: string,
    correctedData: any,
    comments: string
  ) => {
    if (inProgress !== InteractionStatus.None) return;

    const accessTokenRequest = {
      account: accounts[0],
      ...loginRequest,
    };

    setOcrLoading((prev) => new Set(prev).add(fileId));

    try {
      const accessTokenResponse = await instance.acquireTokenSilent(accessTokenRequest);
      const { accessToken, idToken } = accessTokenResponse;

      await submitCorrection(resultId, inviteId, correctedData, comments, accessToken, idToken);
      
      // Update local state
      setOcrResults((prev) => {
        const newMap = new Map(prev);
        const result = newMap.get(fileId);
        if (result) {
          newMap.set(fileId, {
            ...result,
            status: "corrected",
            correctedData,
            correctionComments: comments,
          });
        }
        return newMap;
      });

      setEditingFileId(null);
      console.log("OCR correction saved for fileId:", fileId);
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        instance.acquireTokenRedirect(accessTokenRequest);
      }
      console.error("Error saving OCR correction:", error);
    } finally {
      setOcrLoading((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });
    }
  }, [inviteId, accounts, instance, inProgress]);

  const handleOcrCancelEdit = useCallback(() => {
    setEditingFileId(null);
  }, []);

  return (
    <Box sx={{ mt: 4 }}>
      <Card variant="outlined" sx={{ mb: 4, boxShadow: 1 }}>
        <CardContent>
          <Grid container>
            <Grid item xs={12} md={12}>
              <Box
                component="form"
                sx={{
                  "& > :not(style)": { m: 2 },
                }}
                noValidate
                autoComplete="off"
              >
                <h2>Secure File Request</h2>
                {/* <TextField
                  id="outlined-basic"
                  label="Name"
                  variant="outlined"
                  placeholder="John Doe"
                  helperText="Please enter recipient name"
                  focused
                  disabled={inviteId !== ""}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                /> */}
                <div style={{display:'flex',flexDirection:'row', alignItems:"center", gap:"5em"}}>
                <TextField
                  id="outlined-basic"
                  label="Phone"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">+1</InputAdornment>
                    ),
                  }}
                  disabled={inviteId !== ""}
                  value={address}
                  helperText="Please enter recipient cell phone number"
                  onChange={(e) => setAddress(e.target.value)}
                />
                <Grid item xs={12} md={7} sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 0 } }}>
                  <AgentScript />
                </Grid>
                </div>
                
                <br />
                {!inviteId ? (
                  <Button
                    variant="contained"
                    size="large"
                    disableElevation
                    onClick={sendRequest}
                    disabled={isSending || !isValidPhone(address)}
                  >
                    {isSending ? "Processing ..." : "Send Request"}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      size="large"
                      disableElevation
                      disabled={isSending}
                      onClick={sendSMS}
                    >
                      {isSending ? "Processing ..." : "Resend SMS"}
                    </Button>
                    <Button
                      variant="contained"
                      size="large"
                      disableElevation
                      onClick={() => setConfirmOpen(true)}
                    >
                      Complete
                    </Button>
                    <ConfirmationDialog
                      open={confirmOpen}
                      handleOK={() => {
                        completeRequest();
                        setConfirmOpen(false);
                      }}
                      handleCancel={() => setConfirmOpen(false)}
                    />
                  </>
                )}
                {messages.length > 0 && (
                  <div>
                    <strong>Status:</strong>{" "}
                    <em>
                      {messages[0].body} - {moment(messages[0].time).fromNow()}
                    </em>
                  </div>
                )}
              </Box>
              <Divider orientation="horizontal"></Divider>
              <Box sx={{ m: 2 }}>
                {uploadedFiles.length <= 0 ? (
                  <div>No files have been uploaded</div>
                ) : (
                  <Box>
                    <h3>Uploaded Files</h3>
                    {showImageURL !== "" && (
                      <Lightbox
                        image={showImageURL}
                        onClose={() => setShowImageURL("")}
                      ></Lightbox>
                    )}
                    {uploadedFiles.map(({ url, title, fileId }) => {
                      const ocrResult = fileId ? ocrResults.get(fileId) : undefined;
                      const isEditing = fileId === editingFileId;
                      const isLoading = fileId ? ocrLoading.has(fileId) : false;

                      return (
                        <Box key={url} sx={{ mb: 3 }}>
                          <img
                            src={url}
                            alt={title}
                            height="100"
                            onClick={() => setShowImageURL(url)}
                            style={{
                              cursor: "pointer",
                              marginRight: 10,
                              borderRadius: 5,
                              minHeight: 100,
                              boxShadow:
                                "rgba(0, 0, 0, 0.19) 0px 10px 20px, rgba(0, 0, 0, 0.23) 0px 6px 6px",
                            }}
                          />
                          
                          {/* Display OCR results or correction form */}
                          {ocrResult && fileId && (
                            <Box sx={{ mt: 2 }}>
                              {isEditing && ocrResult.extractedData ? (
                                <OcrCorrectionForm
                                  initialData={ocrResult.correctedData || ocrResult.extractedData}
                                  onSave={(correctedData, comments) =>
                                    handleOcrSaveCorrection(fileId, ocrResult.resultId, correctedData, comments)
                                  }
                                  onCancel={handleOcrCancelEdit}
                                  loading={isLoading}
                                />
                              ) : (
                                <OcrResultsPanel
                                  result={ocrResult}
                                  onApprove={() => handleOcrApprove(fileId, ocrResult.resultId)}
                                  onReject={() => handleOcrReject(fileId, ocrResult.resultId)}
                                  onEdit={() => handleOcrEdit(fileId)}
                                  loading={isLoading}
                                />
                              )}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Grid>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ display: { xs: "none", md: "block" } }}
            ></Divider>
            
          </Grid>
        </CardContent>
      </Card>
      {wssURL && <LogTable wssURL={wssURL} messages={messages} />}
    </Box>
  );
}

const LogTable = (props: LogTableProps) => {
  const { wssURL, messages } = props;

  if (!wssURL) {
    return <h4>Processing ...</h4>;
  } else {
    return (
      <Box>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell width="1%"></TableCell>
                <TableCell>Event Log</TableCell>
                <TableCell align="right" width="20%">
                  Time
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.map((msg: Log, index) => (
                <TableRow
                  key={index}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell scope="row" width="1%">
                    {msg.body.startsWith("Error: " )|| msg.type === 'Error' ? (
                      <Icon sx={{ color: "red" }}>cancel</Icon>
                    ) : (
                      <Icon sx={{ color: "green" }}>check</Icon>
                    )}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {msg.body}
                  </TableCell>
                  <TableCell align="right">
                    {moment(msg.time).fromNow()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }
};

const AgentScript = () => {
  return (
    <div>
      <p>
        <strong>Script for Schedulers: </strong>
        <br />
        <i>
          To best schedule your exam, it would be helpful if you could send me
          an image of your order. To do this, I need your consent for Hoag to
          send you a text message containing a secure link for you to upload an
          image of your order. The text will be sent to the mobile number you
          provide, and data and messaging rates may apply.
        </i>
      </p>
      <p>
        <strong>
          Additional Scheduler script when talking to non-patient/caregivers:{" "}
        </strong>
        <br />
        <i>
          If you are not the patient scheduling this appointment, by providing
          consent, you are acknowledging that you are the patient’s parent,
          legal guardian and/or another individual authorized by the patient to
          send the information on behalf of the patient.
        </i>
      </p>
      <p>
        <strong>Message on actual text: </strong>
        <br />
        This is Hoag Scheduling, please call [1-800-309-9729] if disconnected.
        Click [link to upload file] to upload images. Msg&Data Rates May Apply.
        Reply STOP any time to opt-out of messages.
      </p>
      <p>
        <strong>Message on Website: </strong>
        <br />
        You are now on a secure website where you can securely upload and send
        an image of your order to Hoag. If you wish to stop this process and
        schedule your appointment in another way, please notify the scheduler
        right away. By uploading an image of the order, you are acknowledging
        that you are the patient or the patient’s parent/legal guardian and/or
        another individual authorized by the patient to send the information on
        the patient’s behalf.
      </p>
    </div>
  );
};

type ConfirmationDialogProps = {
  open: boolean;
  handleOK: () => void;
  handleCancel: () => void;
};

const ConfirmationDialog = (props: ConfirmationDialogProps) => {
  return (
    <div>
      <Dialog
        open={props.open}
        onClose={props.handleCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Make sure you save the image before completing this request.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.handleCancel}>Cancel</Button>
          <Button onClick={props.handleOK} autoFocus>
            Yes, Complete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FileRequest;
